class RelatedProductsSlider extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.slider = null
    this.sliderElement = null
    this.observer = null

    this.productsPerView = Number(this.dataset.productsPerView) || 4
    this.productsPerViewMobile =
      Number(this.dataset.productsPerViewMobile) || 2

    this.columnGap = Number(this.dataset.columnGap) || 10
    this.columnGapMobile = Number(this.dataset.columnGapMobile) || 10

    this.paginationStyle = this.dataset.paginationStyle || 'none'
    this.productSource = this.dataset.productSource || 'related'

    this.setupListeners()
    this.setupProducts()
  }

  setupListeners () {
    this.listeners.add(
      this,
      'recently-viewed:loaded',
      () => {
        this.handleRecentlyViewedLoaded()
      },
    )
  }

  setupProducts () {
    if (
      this.productSource === 'related' ||
      this.productSource === 'complementary'
    ) {
      this.setupRecommendations()

      return
    }

    if (this.productSource === 'recently_viewed') {
      this.setupRecentlyViewed()

      return
    }

    this.initialiseSlider()
  }

  setupRecommendations () {
    this.recommendations = this.querySelector(
      'product-recommendations',
    )

    if (!this.recommendations) return

    if (this.getSliderElement()) {
      this.initialiseSlider()

      return
    }

    this.observer = new MutationObserver(() => {
      if (!this.getSliderElement()) return

      this.disconnectObserver()
      this.initialiseSlider()
    })

    this.observer.observe(this.recommendations, {
      childList: true,
      subtree: true,
    })
  }

  setupRecentlyViewed () {
    this.recentlyViewed = this.querySelector(
      'recently-viewed-products',
    )

    if (!this.recentlyViewed) return

    if (
      this.getSliderElement() &&
      this.getSlideCount() > 0
    ) {
      this.initialiseSlider()

      return
    }

    this.observer = new MutationObserver(() => {
      if (!this.getSliderElement()) return
      if (this.getSlideCount() === 0) return

      this.disconnectObserver()
      this.initialiseSlider()
    })

    this.observer.observe(this.recentlyViewed, {
      childList: true,
      subtree: true,
    })
  }

  handleRecentlyViewedLoaded () {
    if (this.productSource !== 'recently_viewed') return

    this.disconnectObserver()
    this.destroySlider()

    window.requestAnimationFrame(() => {
      this.initialiseSlider()
    })
  }

  getSliderElement () {
    return this.querySelector('[data-related-products-slider]')
  }

  getSlideCount () {
    const sliderElement = this.getSliderElement()

    if (!sliderElement) return 0

    return sliderElement.querySelectorAll(
      '.sec-RelatedProducts_Item',
    ).length
  }

  initialiseSlider () {
    if (this.slider) return

    this.sliderElement = this.getSliderElement()

    if (!this.sliderElement) return
    if (this.getSlideCount() === 0) return
    if (typeof window.KeenSlider === 'undefined') return

    this.controlsElement = this.querySelector(
      '[data-related-products-controls]',
    )

    this.dotsElement = this.querySelector(
      '[data-related-products-dots]',
    )

    this.progressBar = this.querySelector(
      '[data-related-products-progress-bar]',
    )

    this.slider = new window.KeenSlider(
      this.sliderElement,
      {
        initial: 0,

        slides: {
          perView: this.productsPerViewMobile,
          spacing: this.columnGapMobile,
        },

        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: this.productsPerView,
              spacing: this.columnGap,
            },
          },
        },

        created: (slider) => {
          this.sliderElement.setAttribute('data-initialised', 'true')
          this.createPagination(slider)
          this.updatePagination(slider)
        },

        slideChanged: (slider) => {
          this.updatePagination(slider)
        },

        optionsChanged: (slider) => {
          this.createPagination(slider)
          this.updatePagination(slider)
        },

        updated: (slider) => {
          this.createPagination(slider)
          this.updatePagination(slider)
        },
      },
      [
        window.BAO.Carousel.plugins.setInitialised(),
        window.BAO.Carousel.plugins.addWheelControls('horizontal'),
      ],
    )
  }

  createPagination (slider) {
    if (this.paginationStyle === 'dots') {
      this.createDots(slider)
    }

    this.updateControls(slider)
  }

  createDots (slider) {
    if (!this.dotsElement) return

    this.dotsElement.innerHTML = ''

    const pageCount = this.getPageCount(slider)

    for (let index = 0; index < pageCount; index += 1) {
      const dot = document.createElement('button')

      dot.type = 'button'
      dot.className = 'sec-RelatedProducts_Dot'

      dot.setAttribute(
        'aria-label',
        `Go to slide ${index + 1}`,
      )

      this.listeners.add(dot, 'click', () => {
        slider.moveToIdx(index)
      })

      this.dotsElement.appendChild(dot)
    }
  }

  updatePagination (slider) {
    this.updateControls(slider)

    if (this.paginationStyle === 'dots') {
      this.updateDots(slider)
    }

    if (this.paginationStyle === 'progress') {
      this.updateProgress(slider)
    }
  }

  updateDots (slider) {
    if (!this.dotsElement) return

    const dots = this.dotsElement.querySelectorAll(
      '.sec-RelatedProducts_Dot',
    )

    const currentIndex = Math.min(
      slider.track.details.rel,
      dots.length - 1,
    )

    dots.forEach((dot, index) => {
      if (index === currentIndex) {
        dot.setAttribute('aria-current', 'true')
      } else {
        dot.removeAttribute('aria-current')
      }
    })
  }

  updateProgress (slider) {
    if (!this.progressBar) return

    const pageCount = this.getPageCount(slider)

    if (pageCount <= 1) {
      this.progressBar.style.inlineSize = '100%'

      return
    }

    const currentIndex = Math.min(
      slider.track.details.rel,
      pageCount - 1,
    )

    const progress =
      ((currentIndex + 1) / pageCount) * 100

    this.progressBar.style.inlineSize = `${progress}%`
  }

  updateControls (slider) {
    if (!this.controlsElement) return

    const pageCount = this.getPageCount(slider)

    this.controlsElement.hidden = pageCount <= 1
  }

  getPageCount (slider) {
    if (!slider.track.details) return 0

    const slideCount = slider.track.details.slides.length

    if (!slideCount) return 0

    const perView = this.getCurrentPerView()

    return Math.max(
      1,
      Math.ceil(slideCount - perView + 1),
    )
  }

  getCurrentPerView () {
    return window.matchMedia('(min-width: 768px)').matches
      ? this.productsPerView
      : this.productsPerViewMobile
  }

  disconnectObserver () {
    if (!this.observer) return

    this.observer.disconnect()
    this.observer = null
  }

  destroySlider () {
    if (!this.slider) return

    this.slider.destroy()
    this.slider = null

    if (this.sliderElement) {
      this.sliderElement.removeAttribute('data-initialised')
      this.sliderElement = null
    }

    if (this.dotsElement) {
      this.dotsElement.innerHTML = ''
    }

    if (this.progressBar) {
      this.progressBar.style.inlineSize = '0%'
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback?.()

    this.destroySlider()
    this.disconnectObserver()
  }
}

if (!customElements.get('related-products-slider')) {
  customElements.define(
    'related-products-slider',
    RelatedProductsSlider,
  )
}
